import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtTime12, isLate } from "@/lib/utils";
import { announceAttendance, announceReject } from "@/lib/sound";
import { ScanLine, CheckCircle2, XCircle, Clock, Nfc, Smartphone, X, Copy, Fingerprint, ScanFace, Monitor } from "lucide-react";
import { toast } from "sonner";

function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(i);
  }, []);
  const h24 = now.getHours();
  const ampm = h24 >= 12 ? "pm" : "am";
  const h12 = h24 % 12 || 12;
  const hh = String(h12).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");
  return (
    <div className="bg-navy text-white rounded-md px-5 py-3 inline-flex items-center gap-3" data-testid="live-clock-scan">
      <Clock className="w-4 h-4 text-emerald-300" />
      <div className="font-mono font-bold text-2xl tracking-[0.15em] leading-none">{hh}:{mm}<span className="text-emerald-300">:{ss}</span> <span className="text-emerald-300 text-lg">{ampm}</span></div>
    </div>
  );
}

export default function ScanStation() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const lateFor = (k) => (k === "teacher" ? user?.late_after_teacher : user?.late_after);
  const [buffer, setBuffer] = useState("");
  const [lastResult, setLastResult] = useState(null);
  const [recent, setRecent] = useState([]);
  const [flash, setFlash] = useState(null); // "ok" | "err"
  const [nfcSupported, setNfcSupported] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [showMobile, setShowMobile] = useState(false);
  const [readerMode, setReaderMode] = useState("card"); // "card" | "fingerprint" | "facial"
  const inputRef = useRef(null);

  const waitingLabel = readerMode === "fingerprint" ? "Esperando huella…" : readerMode === "facial" ? "Esperando rostro…" : "Esperando tarjeta…";

  const activateReader = (mode) => {
    setReaderMode(mode);
    inputRef.current?.focus();
    const msg = mode === "fingerprint"
      ? "Lector de huella activado. Vincula tu lector por USB o Bluetooth (modo teclado) y coloca el dedo; el ID de la persona se captura solo."
      : "Lector facial activado. Vincula tu dispositivo por USB o Bluetooth (modo teclado) y mira la cámara; el ID de la persona se captura solo.";
    toast.info(msg);
  };

  useEffect(() => {
    setNfcSupported(typeof window !== "undefined" && "NDEFReader" in window);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => inputRef.current?.focus(), 800);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
    (async () => {
      try {
        const { data } = await api.get("/attendance/today");
        setRecent(data.slice(0, 100));
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const handleScan = async (uid) => {
    const cleaned = uid.trim();
    if (!cleaned) return;
    try {
      const { data } = await api.post("/attendance/scan", { nfc_uid: cleaned });
      setLastResult({ ok: true, data });
      setFlash("ok"); setTimeout(() => setFlash(null), 900);
      setRecent((prev) => [data, ...prev].slice(0, 100));
      announceAttendance(data.student_name, data.type);
    } catch (e) {
      setLastResult({ ok: false, message: e.response?.data?.detail || "Error" });
      setFlash("err"); setTimeout(() => setFlash(null), 900);
      announceReject();
    } finally {
      setBuffer("");
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleScan(buffer);
    }
  };

  const startNfcRead = async () => {
    if (!nfcSupported) {
      inputRef.current?.focus();
      toast.info("Acerca la tarjeta a tu lector USB; el UID se captura solo. Para celulares usa 'Registrar con celular'.");
      return;
    }
    try {
      const ndef = new window.NDEFReader();
      await ndef.scan();
      setScanning(true);
      toast.success("Lectura activada. Acerca la tarjeta NFC…");
      ndef.onreading = (event) => {
        const uid = (event.serialNumber || "").replace(/:/g, "").toUpperCase();
        if (uid) handleScan(uid);
      };
      ndef.onreadingerror = () => toast.error("No se pudo leer la tarjeta. Intenta de nuevo.");
    } catch (e) {
      setScanning(false);
      toast.error("No se pudo iniciar la lectura NFC. Revisa los permisos del navegador.");
    }
  };

  const tapBaseUrl = `${window.location.origin}/tap/`;
  const copyTapBase = () => {
    navigator.clipboard.writeText(tapBaseUrl);
    toast.success("URL base copiada");
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="scan-page">
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-navy-light mb-3 flex items-center gap-2">
            <span className="w-8 h-px bg-navy" /> Modo lectura
          </div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-navy">Estación de registro</h1>
          <p className="text-sm text-nardo-500 mt-2">Registra con tarjeta NFC, lector de huella o lector facial por USB o Bluetooth. El ID se captura automáticamente y se registra entrada/salida.</p>
        </div>
        <LiveClock />
      </div>

      <div className="mb-8">
        <button
          onClick={() => navigate("/kiosk")}
          data-testid="open-kiosk-button"
          className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white px-5 py-3 rounded-md transition-colors text-sm font-medium"
        >
          <Monitor className="w-4 h-4" strokeWidth={2} /> Modo kiosko (iPad / pantalla de entrada)
        </button>
        <span className="ml-3 text-[11px] text-nardo-400 align-middle">Pantalla completa con cámara QR + lector NFC para la entrada.</span>
      </div>

      <div className={`relative bg-white border border-nardo-200 rounded-md p-6 sm:p-10 transition-colors ${flash === "ok" ? "animate-flash-success" : ""} ${flash === "err" ? "bg-red-50/40" : ""}`}>
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-navy" />
        <div className="flex flex-col items-center gap-6">
          <ScanLine className="w-12 h-12 text-navy-light" strokeWidth={1.25} />
          <div className="text-[10px] uppercase tracking-[0.3em] text-nardo-500" data-testid="waiting-label">{waitingLabel}</div>
          <input
            ref={inputRef}
            value={buffer}
            onChange={(e) => setBuffer(e.target.value.toUpperCase())}
            onKeyDown={onKeyDown}
            data-testid="nfc-scan-input"
            autoFocus
            placeholder="— — — —"
            className="w-full max-w-2xl text-center text-3xl sm:text-4xl font-mono tracking-[0.4em] text-navy bg-nardo-50 border-2 border-dashed border-nardo-300 p-8 sm:p-12 rounded-lg focus:border-navy focus:bg-navy/5 transition-all outline-none uppercase"
          />
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => { setReaderMode("card"); startNfcRead(); }}
              data-testid="read-card-button"
              className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white px-6 py-3 rounded-md transition-colors text-sm font-medium"
            >
              <Nfc className="w-4 h-4" strokeWidth={2} /> {scanning ? "Leyendo… acerca la tarjeta" : "Leer tarjeta"}
            </button>
            <button
              onClick={() => activateReader("fingerprint")}
              data-testid="fingerprint-reader-button"
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-md transition-colors text-sm font-medium border ${readerMode === "fingerprint" ? "bg-navy text-white border-navy" : "text-navy border-nardo-300 hover:border-navy hover:bg-nardo-50"}`}
            >
              <Fingerprint className="w-4 h-4" /> Lector de huella digital
            </button>
            <button
              onClick={() => activateReader("facial")}
              data-testid="facial-reader-button"
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-md transition-colors text-sm font-medium border ${readerMode === "facial" ? "bg-navy text-white border-navy" : "text-navy border-nardo-300 hover:border-navy hover:bg-nardo-50"}`}
            >
              <ScanFace className="w-4 h-4" /> Lector facial
            </button>
            <button
              onClick={() => handleScan(buffer)}
              data-testid="manual-scan-button"
              className="text-xs uppercase tracking-[0.25em] text-nardo-600 hover:text-navy border border-nardo-300 hover:border-navy px-5 py-2.5 rounded-md transition-colors"
            >Registrar manualmente</button>
          </div>
          <p className="text-[11px] text-nardo-400 text-center max-w-md">
            Compatible con lectores de tarjeta, huella o rostro en <strong>modo teclado</strong> (USB o Bluetooth HID) que envían el UID de la persona. El dispositivo realiza el reconocimiento y envía el mismo UID registrado en el alumno o docente.
          </p>
          {!nfcSupported && (
            <p className="text-[11px] text-nardo-400 text-center max-w-md">
              Tu navegador no soporta lectura NFC directa. Usa el lector USB o Bluetooth (captura automática) o el{" "}
              <button onClick={() => setShowMobile(true)} data-testid="mobile-config-link" className="underline underline-offset-2 hover:text-navy">registro por celular con NFC Tools</button>.
            </p>
          )}
          {nfcSupported && (
            <button onClick={() => setShowMobile(true)} data-testid="mobile-config-link" className="text-[11px] uppercase tracking-[0.2em] text-nardo-500 hover:text-navy underline-offset-2 hover:underline">
              Registrar con celular (NFC Tools)
            </button>
          )}
        </div>
      </div>

      {lastResult && (
        <div className="mt-6 animate-slide-up" data-testid="scan-result">
          {lastResult.ok ? (() => {
            const isIn = lastResult.data.type === "in";
            const late = isIn && isLate(lastResult.data.timestamp, lateFor(lastResult.data.kind));
            const iconColor = !isIn ? "text-blue-300" : late ? "text-orange-300" : "text-emerald-300";
            const label = !isIn ? "Salida registrada" : late ? "Entrada con retardo" : "Entrada registrada";
            return (
            <div className="bg-navy text-white rounded-md p-6 flex items-center gap-5">
              <CheckCircle2 className={`w-10 h-10 shrink-0 ${iconColor}`} />
              <div>
                <div className={`text-[10px] uppercase tracking-[0.3em] mb-1 font-semibold ${iconColor}`} data-testid="scan-result-label">
                  {label}
                </div>
                <div className="font-display font-semibold text-2xl">{lastResult.data.student_name}</div>
                <div className="text-xs text-nardo-300 font-mono mt-1">{fmtTime12(lastResult.data.timestamp)}</div>
              </div>
            </div>
            );
          })() : (
            <div className="bg-red-50 border border-red-200 rounded-md p-6 flex items-center gap-4">
              <XCircle className="w-8 h-8 text-red-500 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-red-600 mb-1">Error</div>
                <div className="text-sm text-red-700">{lastResult.message}</div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-nardo-500 mb-3">Asistencia de hoy</div>
        <div className="bg-white border border-nardo-200 rounded-md divide-y divide-nardo-100">
          {(() => {
            const grouped = {};
            recent.forEach((r) => {
              if (!grouped[r.student_id]) grouped[r.student_id] = { id: r.student_id, name: r.student_name, kind: r.kind, in: null, out: null };
              if (r.type === "in" && !grouped[r.student_id].in) grouped[r.student_id].in = r.timestamp;
              if (r.type === "out") grouped[r.student_id].out = r.timestamp;
            });
            const rows = Object.values(grouped).sort((a, b) => (b.out || b.in || "").localeCompare(a.out || a.in || ""));
            if (rows.length === 0) return <div className="px-6 py-8 text-center text-sm text-nardo-500">Sin actividad aún</div>;
            return rows.slice(0, 20).map((p) => (
              <div key={p.id} className="px-6 py-3 flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded ${p.kind === "teacher" ? "bg-navy-light/15 text-navy-light" : "bg-navy/10 text-navy"}`}>{p.kind === "teacher" ? "M" : "A"}</span>
                  <span className="text-navy font-medium">{p.name}</span>
                </div>
                <div className="flex items-center gap-4 text-xs font-mono">
                  <span className={p.in ? (isLate(p.in, lateFor(p.kind)) ? "text-orange-600" : "text-emerald-700") : "text-nardo-300"}>↓ {p.in ? fmtTime12(p.in) : "—"}</span>
                  <span className={p.out ? "text-blue-600" : "text-nardo-300"}>↑ {p.out ? fmtTime12(p.out) : "—"}</span>
                </div>
              </div>
            ));
          })()}
        </div>
      </div>

      {showMobile && (
        <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowMobile(false)}>
          <div className="bg-white border border-nardo-200 rounded-md w-full max-w-lg p-8 relative animate-slide-up max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mobile-config-modal">
            <button onClick={() => setShowMobile(false)} className="absolute top-4 right-4 text-nardo-500 hover:text-navy" data-testid="mobile-config-close"><X className="w-4 h-4" /></button>
            <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
            <div className="flex items-center gap-2 mb-1">
              <Smartphone className="w-4 h-4 text-navy" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-navy">Registro por celular</div>
            </div>
            <h2 className="font-display font-bold text-2xl text-navy mb-2">Usar cualquier celular con NFC</h2>
            <p className="text-sm text-nardo-500 mb-5">
              Con la app gratuita <strong>NFC Tools</strong> (iOS y Android) graba en cada tarjeta un registro de tipo <strong>URL</strong>.
              Al acercar la tarjeta a cualquier celular con NFC, se abrirá el enlace y la entrada/salida quedará registrada al instante.
            </p>

            <ol className="space-y-3 text-sm text-nardo-700 mb-6 list-decimal pl-5">
              <li>Instala <strong>NFC Tools</strong> desde App Store o Google Play.</li>
              <li>En la pestaña <strong>Escribir → Añadir un registro → URL/URI</strong>.</li>
              <li>Pega el enlace de programación de cada persona (botón <strong>copiar link</strong> en Alumnos/Docentes), reemplazando <code className="font-mono">/nfc/program/</code> por <code className="font-mono">/tap/</code>.</li>
              <li>Acerca la tarjeta y pulsa <strong>Escribir</strong>. ¡Listo!</li>
            </ol>

            <div className="bg-nardo-50 border border-nardo-200 rounded-md p-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-nardo-500 mb-2">URL base de registro</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs font-mono text-navy break-all" data-testid="tap-base-url">{tapBaseUrl}&lt;código-de-la-persona&gt;</code>
                <button onClick={copyTapBase} className="p-2 text-nardo-500 hover:text-navy shrink-0" title="Copiar URL base" data-testid="copy-tap-base">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[11px] text-nardo-400 mt-2">
                Cada persona tiene su propio código (el mismo del link de programación). En iPhone, al tocar la tarjeta se abre Safari y se registra automáticamente.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
